import { http } from "@/api/http";
import type {
  Application,
  ApplicationHistoryRecord,
  TrackingOverview,
  TrackingStatus,
} from "@/types/application";
import type { BulkApplyJob, BulkApplyMode, BulkApplyResponse } from "@/types/bulkApply";

function trackingPath(id: string, action: "status" | "history"): string {
  return `/tracking/${encodeURIComponent(id)}/${action}`;
}

export async function getTrackingOverview(signal?: AbortSignal): Promise<TrackingOverview> {
  const config = signal ? { signal } : {};
  const response = await http.get<TrackingOverview>("/tracking", config);
  return response.data;
}

export async function updateTrackingStatus(
  applicationId: string,
  trackingStatus: TrackingStatus,
): Promise<Application> {
  const response = await http.post<{ application: Application }>(trackingPath(applicationId, "status"), {
    tracking_status: trackingStatus,
  });
  return response.data.application;
}

export async function archiveApplication(applicationId: string): Promise<ApplicationHistoryRecord> {
  const response = await http.post<{ history: ApplicationHistoryRecord }>(
    trackingPath(applicationId, "history"),
    {},
  );
  return response.data.history;
}

export async function startBulkApply(
  applicationIds: string[],
  mode: BulkApplyMode,
): Promise<BulkApplyJob> {
  const response = await http.post<BulkApplyResponse>(
    "/tracking/bulk-apply",
    { application_ids: applicationIds, mode },
    { timeout: 30_000 },
  );
  return response.data.job;
}

export async function getBulkApplyStatus(jobId: string, signal?: AbortSignal): Promise<BulkApplyJob> {
  const params = { job_id: jobId };
  const config = signal ? { params, signal } : { params };
  const response = await http.get<{ job: BulkApplyJob }>("/tracking/bulk-apply/status", config);
  return response.data.job;
}

export async function getActiveBulkApply(signal?: AbortSignal): Promise<BulkApplyJob | null> {
  const config = signal ? { signal } : {};
  const response = await http.get<{ job: BulkApplyJob | null }>(
    "/tracking/bulk-apply/active",
    config,
  );
  return response.data.job;
}

export async function resumeBulkApply(jobId: string): Promise<BulkApplyJob> {
  const response = await http.post<BulkApplyResponse>("/tracking/bulk-apply/resume", {
    job_id: jobId,
  });
  return response.data.job;
}

export async function cancelBulkApply(jobId: string): Promise<BulkApplyJob> {
  const response = await http.post<BulkApplyResponse>("/tracking/bulk-apply/cancel", {
    job_id: jobId,
  });
  return response.data.job;
}
